import { Box, Typography } from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { addDays, format, startOfDay, startOfHour } from 'date-fns';
import { useMemo } from 'react';
import { WalletLog } from '../../../apiTypes';
import { chartColors1, chartColors2 } from '../../../constants/charts';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { Lang, useLocale } from '../../../contexts/locale.context';
import currency from '../../../utils/currency';
import { dateLocaleMap } from '../../../utils/dates';
import useMedia from '../../../utils/useMedia';
import { TimeScale } from '../Reports';

export const getChartData = (logs: WalletLog[], lang: Lang): Record<string, number> => {
  const incomeKey = t.income[lang];
  const spendKey = t.expenses[lang];

  const data = {
    [incomeKey]: 0,
    [spendKey]: 0,
  };

  logs.forEach((log) => {
    if (log.sum > 0) {
      data[incomeKey] += log.sum;
    } else {
      data[spendKey] -= log.sum;
    }

    if (log.bankCommission) {
      data[spendKey] += log.bankCommission;
    }
  });

  return data;
};

function groupLogsByHour(logs: WalletLog[]): Record<string, WalletLog[]> {
  const grouped: Record<string, WalletLog[]> = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const log of logs) {
    const hour = startOfHour(log.time);
    const hourKey = hour.getTime();
    grouped[hourKey] = [...(grouped[hourKey] || []), log];
  }

  return grouped;
}

function groupLogsByDay(logs: WalletLog[]): Record<string, WalletLog[]> {
  const grouped: Record<string, WalletLog[]> = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const log of logs) {
    const day = startOfDay(log.time);
    const dayKey = day.getTime();

    grouped[dayKey] = [...(grouped[dayKey] || []), log];
  }

  return grouped;
}

function groupLogsByDate(logs: WalletLog[]): Record<string, WalletLog[]> {
  const grouped: Record<string, WalletLog[]> = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const log of logs) {
    const dayKey = format(log.time, 'dd.MM');

    grouped[dayKey] = [...(grouped[dayKey] || []), log];
  }

  return grouped;
}

function prepareWeeklyChartData(logs: WalletLog[], lang: Lang, timeRange: [number, number]) {
  const lastWeek = {
    start: new Date(timeRange[0]),
    end: new Date(timeRange[1]),
  };

  const groupedLogsByDay = groupLogsByDay(logs);

  const chartData = Array.from({ length: 7 }, (_, i) => {
    const dayKey = startOfDay(addDays(lastWeek.start, i)).getTime();
    const reportRowsMap = getChartData(groupedLogsByDay[dayKey] || [], lang);

    return {
      title: format(dayKey, 'EEE', { locale: dateLocaleMap[lang] }),
      ...reportRowsMap,
    };
  });

  return chartData;
}

function prepareMonthlyChartData(logs: WalletLog[], lang: Lang, timeRange: [number, number]) {
  const groupedLogsByDate = groupLogsByDate(logs);

  const chartData = Array.from({ length: 31 }, (_, i) => {
    const dayKey = addDays(timeRange[0], i).getTime();
    const reportRowsMap = getChartData(groupedLogsByDate[format(dayKey, 'dd.MM')] || [], lang);

    return {
      title: format(dayKey, 'dd.MM', { locale: dateLocaleMap[lang] }),
      ...reportRowsMap,
    };
  });

  return chartData;
}

function prepareChartData(logs: WalletLog[], lang: Lang) {
  const groupedByHours = groupLogsByHour(logs);

  const chartData = Array.from({ length: 24 }, (_, i) => {
    const hourKey = startOfHour(new Date(logs[0]?.time || 0).setHours(i)).getTime();
    const reportRowsMap = getChartData(groupedByHours[hourKey] || [], lang);
    return {
      title: format(hourKey, 'HH:mm'),
      ...reportRowsMap,
    };
  });

  return chartData;
}

type Props = {
  timeScale: TimeScale;
  logs: WalletLog[];
  timeRange: [number, number];
};

const WalletsChart = ({ timeScale, logs = [], timeRange }: Props) => {
  const { lang } = useLocale();
  const { cafe } = useAdmin();
  const { isDesktop } = useMedia();

  const data = useMemo(() => {
    if (timeScale === 'day') {
      return prepareChartData(logs, lang);
    }

    if (timeScale === 'week') {
      return prepareWeeklyChartData(logs, lang, timeRange);
    }
    return prepareMonthlyChartData(logs, lang, timeRange);
  }, [lang, logs, timeScale, timeRange]);

  const keys = useMemo(() => Object.keys(data[0]!).filter((v) => v !== 'title'), [data]);

  return (
    <>
      <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 10 }}>
        <ResponsiveBar
          data={data}
          keys={keys}
          indexBy="title"
          margin={{ bottom: 40, top: 0, left: 0, right: 0 }}
          borderRadius={4}
          innerPadding={2}
          valueFormat={(v) => (v ? `${currency(v, cafe?.currency, true)}` : '')}
          enableLabel={false}
          axisLeft={null}
          axisBottom={{ tickRotation: isDesktop || timeScale === 'week' ? 0 : -90 }}
          groupMode="grouped"
          padding={0.5}
          enableGridY={false}
          colors={[chartColors1[0]!, chartColors2[0]!]}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4, flexWrap: 'wrap', px: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mr: 2 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: chartColors1[0], borderRadius: '2px' }} />
          <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
            {t.income[lang]}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mr: 2 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: chartColors2[0], borderRadius: '2px' }} />
          <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
            {t.expenses[lang]}
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default WalletsChart;
